import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DAYS, ALL_SUBJECTS } from "@/lib/constants";
import { Loader2, X, Plus, CalendarRange, Trash2 } from "lucide-react";

const DAY_SHORT = { "Pazartesi": "PZT", "Salı": "SAL", "Çarşamba": "ÇAR", "Perşembe": "PER", "Cuma": "CUM", "Cumartesi": "CMT", "Pazar": "PAZ" };
const TYPES = ["Föy", "Soru Bankası", "Deneme"];
const EMPTY_DAY = { unit: "", pages: "", total: "" };

export default function LibraryProgramModal({ resources, tracks, onClose }) {
  const [resId, setResId] = useState("");
  const [detail, setDetail] = useState(null);
  const [resType, setResType] = useState("Föy");
  const [tracksSel, setTracksSel] = useState([]); // en fazla 3 sınıf/alan
  const toggleTrack = (t) =>
    setTracksSel((prev) => {
      if (prev.includes(t)) return prev.filter((x) => x !== t);
      if (prev.length >= 3) {
        toast.error("En fazla 3 sınıf/alan seçebilirsiniz");
        return prev;
      }
      return [...prev, t];
    });
  const [days, setDays] = useState([]);
  const [startWeek, setStartWeek] = useState(1);
  const [activeWeek, setActiveWeek] = useState(1);
  const [cells, setCells] = useState([]);
  const [manualRows, setManualRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const pick = async (id) => {
    setResId(id);
    setDetail(null);
    setCells([]);
    setManualRows([]);
    if (!id) return;
    try {
      const { data } = await api.get(`/library-resources/${id}`);
      setDetail(data);
      setResType(data.res_type || "Föy");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  useEffect(() => {
    if (!detail || days.length === 0) { setCells([]); return; }
    const secs = detail.sections || [];
    const pub = detail.publisher ? `${detail.publisher} · ` : "";
    const sw = Number(startWeek) || 1;
    setCells(secs.map((s, i) => ({
      key: `a${i}`,
      week: sw + Math.floor(i / days.length),
      day: days[i % days.length],
      subject: s.subject || "",
      unit_name: s.name || "",
      pages: s.pages || s.bank_pages || "",
      total: Number(s.questions || 0),
      section_type: resType,
      resource_name: `${pub}${detail.name} (${resType})`,
      publisher: detail.publisher || "",
    })));
    setActiveWeek(sw);
  }, [detail, days, resType, startWeek]);

  const toggleDay = (d) => setDays((s) => (s.includes(d) ? s.filter((x) => x !== d) : DAYS.filter((x) => [...s, d].includes(x))));

  const sw = Number(startWeek) || 1;
  const weeks = [...new Set([...cells.map((c) => c.week), ...manualRows.map((r) => r.week)])].sort((a, b) => a - b);
  const curWeek = weeks.includes(activeWeek) ? activeWeek : (weeks[0] || sw);
  const wCells = cells.filter((c) => c.week === curWeek);
  const wManual = manualRows.filter((r) => r.week === curWeek);
  const subjects = [...new Set([...wCells.map((c) => c.subject), ...wManual.map((r) => r.subject)].filter(Boolean))];

  const upd = (key, field, val) => setCells((cs) => cs.map((c) => (c.key === key ? { ...c, [field]: val } : c)));
  const updManual = (id, field, val) => setManualRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  const updManualDay = (id, day, field, val) => setManualRows((rs) => rs.map((r) => (r.id === id ? { ...r, days: { ...r.days, [day]: { ...(r.days[day] || EMPTY_DAY), [field]: val } } } : r)));

  const addRow = () => setManualRows((rs) => [...rs, { id: `m${Date.now()}`, week: curWeek, subject: "", days: {} }]);

  const save = async () => {
    if (tracksSel.length === 0) return toast.error("Sınıf / Alan seçin");
    const entries = cells.filter((c) => c.subject && c.unit_name).map(({ key, ...c }) => ({ ...c, total: Number(c.total || 0) }));
    manualRows.forEach((r) => {
      if (!r.subject) return;
      DAYS.forEach((d) => {
        const c = r.days[d];
        if (c && (c.unit || c.pages || c.total)) {
          entries.push({ week: r.week, day: d, subject: r.subject, unit_name: c.unit || r.subject, pages: c.pages || "", total: Number(c.total || 0), section_type: resType, resource_name: detail ? `${detail.publisher ? detail.publisher + " · " : ""}${detail.name} (${resType})` : r.subject, publisher: detail?.publisher || "" });
        }
      });
    });
    if (!entries.length) return toast.error("Önce kaynak seçip gün işaretleyin ya da ders satırı doldurun");
    setBusy(true);
    try {
      const { data } = await api.post("/program/assign-entries", { target: "track", tracks: tracksSel, entries });
      toast.success(`"${tracksSel.join(", ")}" alanındaki ${data.students} öğrenciye ${data.created} görev atandı`);
      onClose();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const DayCell = ({ entry, testid }) => (
    <div className="space-y-1" data-testid={testid}>
      <input className="ta-input !py-1 !px-1.5 text-[11px] text-center" placeholder="1. Ünite" value={entry.unit_name} onChange={(e) => upd(entry.key, "unit_name", e.target.value)} data-testid={`${testid}-unit`} />
      <input className="ta-input !py-1 !px-1.5 text-[11px] text-center" placeholder="8-20" value={entry.pages} onChange={(e) => upd(entry.key, "pages", e.target.value)} data-testid={`${testid}-pages`} />
      <input type="number" className="ta-input !py-1 !px-1.5 text-[11px] text-center" placeholder="soru" value={entry.total || ""} onChange={(e) => upd(entry.key, "total", e.target.value)} data-testid={`${testid}-total`} />
      <div className="flex gap-1">
        {["D", "Y", "B"].map((l) => (
          <input key={l} disabled className="ta-input !py-1 !px-0.5 text-[10px] text-center flex-1 min-w-0 bg-slate-50 text-slate-400" placeholder={l} title="Öğrenci doldurur" />
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="lp-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-extrabold text-slate-900 flex items-center gap-2"><CalendarRange size={20} className="text-indigo-700" /> Yeni Çalışma Programı</h2>
            <p className="text-sm text-slate-500 mt-0.5">Kütüphaneden kaynak seç, türünü ve sınıfını belirle, haftanın günlerini işaretle — bölümler günlere bölünüp haftalar otomatik oluşur.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="lp-close"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2">
            <label className="ta-label">Kaynak / Program Adı</label>
            <select className="ta-input" value={resId} onChange={(e) => pick(e.target.value)} data-testid="lp-resource">
              <option value="">Kütüphaneden seç — örn. HIZ YAYINLARI TARİH SORU BANKASI</option>
              {resources.map((r) => <option key={r.id} value={r.id}>{r.publisher ? `${r.publisher} · ` : ""}{r.name} ({r.sections} bölüm)</option>)}
            </select>
          </div>
          <div>
            <label className="ta-label">Tür</label>
            <select className="ta-input" value={resType} onChange={(e) => setResType(e.target.value)} data-testid="lp-type">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="ta-label">Sınıf / Alan (en fazla 3)</label>
            <div className="flex flex-wrap gap-1.5 rounded-lg border border-slate-200 bg-white p-2" data-testid="lp-track-chips">
              {tracks.map((t) => (
                <button type="button" key={t} onClick={() => toggleTrack(t)} data-testid={`lp-track-chip-${t}`}
                  className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${tracksSel.includes(t) ? "bg-indigo-900 text-white border-indigo-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
                  {t}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-slate-400 mt-1">{tracksSel.length}/3 seçili</div>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="ta-label">Haftanın Günleri (bölümler sırayla bu günlere bölünür)</label>
            <div className="flex flex-wrap gap-1.5" data-testid="lp-days">
              {DAYS.map((d) => (
                <button key={d} onClick={() => toggleDay(d)} data-testid={`lp-day-${d}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${days.includes(d) ? "bg-indigo-900 text-white border-indigo-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                  {DAY_SHORT[d]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="ta-label">Başlangıç Haftası</label>
            <input type="number" min="1" className="ta-input w-24" value={startWeek} onChange={(e) => setStartWeek(e.target.value)} data-testid="lp-start-week" />
          </div>
          {detail && days.length > 0 && (
            <div className="text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2" data-testid="lp-weeks-info">
              {(detail.sections || []).length} bölüm ÷ {days.length} gün → {weeks.length} hafta
            </div>
          )}
        </div>

        {weeks.length > 0 && (
          <>
            <div className="flex flex-wrap gap-2" data-testid="lp-week-tabs">
              {weeks.map((w) => (
                <button key={w} onClick={() => setActiveWeek(w)} data-testid={`lp-week-${w}`}
                  className={`px-3.5 py-1.5 rounded-xl text-sm font-bold transition-colors ${curWeek === w ? "bg-indigo-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {w}. Hafta
                </button>
              ))}
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-bold text-slate-800">{curWeek}. Hafta — Ders Satırları × 7 Gün</div>
                <button onClick={addRow} className="text-xs font-semibold text-indigo-600 flex items-center gap-1" data-testid="lp-add-row"><Plus size={14} /> Ders Satırı Ekle</button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[1000px]">
                  <thead>
                    <tr>
                      <th className="text-left text-[11px] font-bold text-slate-400 uppercase pb-2 w-36">Ders</th>
                      {DAYS.map((d) => <th key={d} className="text-center text-[11px] font-bold text-slate-400 uppercase pb-2">{DAY_SHORT[d]}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.length === 0 && wManual.length === 0 && (
                      <tr><td colSpan={8} className="py-8 text-center text-slate-400 text-sm">Bu hafta boş.</td></tr>
                    )}
                    {subjects.filter((s) => wCells.some((c) => c.subject === s)).map((sub) => (
                      <tr key={sub} className="border-t border-slate-100" data-testid={`lp-row-${sub}`}>
                        <td className="pr-2 py-2 font-bold text-slate-800 text-sm align-top pt-4">{sub}</td>
                        {DAYS.map((d) => {
                          const list = wCells.filter((c) => c.subject === sub && c.day === d);
                          return (
                            <td key={d} className="p-1 align-top min-w-[120px]">
                              {list.length === 0 ? (
                                <div className="text-center text-slate-200 text-xs py-6">—</div>
                              ) : (
                                <div className="space-y-2">{list.map((c) => <DayCell key={c.key} entry={c} testid={`lp-cell-${c.key}`} />)}</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {wManual.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100" data-testid={`lp-manual-${r.id}`}>
                        <td className="pr-2 py-2 align-top">
                          <select className="ta-input !py-1.5 text-xs" value={r.subject} onChange={(e) => updManual(r.id, "subject", e.target.value)} data-testid={`lp-manual-subject-${r.id}`}>
                            <option value="">Ders</option>
                            {ALL_SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <button onClick={() => setManualRows((rs) => rs.filter((x) => x.id !== r.id))} className="mt-1 text-rose-400 hover:text-rose-600" data-testid={`lp-manual-del-${r.id}`}><Trash2 size={13} /></button>
                        </td>
                        {DAYS.map((d) => {
                          const c = r.days[d] || EMPTY_DAY;
                          return (
                            <td key={d} className="p-1 align-top min-w-[120px]">
                              <div className="space-y-1">
                                <input className="ta-input !py-1 !px-1.5 text-[11px] text-center" placeholder="1. Ünite" value={c.unit} onChange={(e) => updManualDay(r.id, d, "unit", e.target.value)} data-testid={`lp-manual-${r.id}-${d}-unit`} />
                                <input className="ta-input !py-1 !px-1.5 text-[11px] text-center" placeholder="8-20" value={c.pages} onChange={(e) => updManualDay(r.id, d, "pages", e.target.value)} />
                                <input type="number" className="ta-input !py-1 !px-1.5 text-[11px] text-center" placeholder="soru" value={c.total} onChange={(e) => updManualDay(r.id, d, "total", e.target.value)} />
                                <div className="flex gap-1">
                                  {["D", "Y", "B"].map((l) => (
                                    <input key={l} disabled className="ta-input !py-1 !px-0.5 text-[10px] text-center flex-1 min-w-0 bg-slate-50 text-slate-400" placeholder={l} title="Öğrenci doldurur" />
                                  ))}
                                </div>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <Button onClick={save} disabled={busy} className="w-full h-12 bg-indigo-900 hover:bg-indigo-800 text-base" data-testid="lp-save">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />} <span className="ml-1">Programı Kaydet</span>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
